// src/services/contentModeration.ts
import { logger } from '../utils/logger';
// Content moderation utility for filtering harmful content
// NOTE: This is an adult platform - we only filter hate speech, slurs, and harmful content
// General profanity is allowed

/**
 * List of banned words/phrases - HATE SPEECH AND SLURS ONLY
 * Keep this list lowercase - matching is case-insensitive
 */
const BANNED_WORDS = [
  // Racial slurs
  'nigger', 'nigga', 'nig nog', 'negro',
  'chink', 'gook', 'zipperhead',
  'kike', 'yid',
  'spic', 'beaner', 'wetback',
  'towelhead', 'sand nigger', 'raghead',
  'paki',
  'wog', 'coon', 'jigaboo', 'pickaninny',

  // Homophobic/transphobic slurs
  'faggot', 'tranny', 'shemale',

  // Ableist slurs (severe ones)
  'retard', 'retarded', 'mongoloid',

  // Nazi/hate symbols and calls (text versions)
  'sieg heil', 'heil hitler', 'gas the',
  '1488', '14/88', 'day of the rope',
  'race war', 'white power', 'white pride',
  'black crime', 'jogger', 'dindu',

  // Violent threats
  'kill yourself', 'kys', 'kill urself', 'an hero',
  'rope yourself', 'neck yourself',

  // Child exploitation terms
  'pedo', 'pedophile', 'child porn', 'cp', 'loli', 'lolicon',
  'jailbait', 'preteen', 'underage',
];

/**
 * Patterns that indicate potential hate speech or harmful content
 * These use regex for more flexible matching
 */
const SUSPICIOUS_PATTERNS = [
  /\b(n+i+g+a*|n+i+g+e+r+)\b/i,  // n-word variations
  /\b(f+[a4@]+g+|f+[a4@]+g+[o0]+t+)\b/i,  // f-slur variations
  /\b(k+y+s+|k+i+l+l+\s*y+o+u+r+s+e+l+f+)\b/i,  // kys variations
  /\b(tr+[a4@]+n+y+|tr+[a4@]+n+e+)\b/i,  // t-slur variations
  /\b(g+a+s+\s+t+h+e+\s+j+e+w+s*)\b/i,  // nazi phrases
  /\b88\b/,  // nazi symbol (but only standalone to avoid false positives)
  /卐|卍/,  // swastika symbols
  /\b(ch+i+l+d+\s*p+o+r+n+)\b/i,  // CP references
];

/**
 * Check if text contains banned words or suspicious patterns
 * @param text - The text to check
 * @returns Object with isClean flag and reason if flagged
 */
export function moderateContent(text: string): {
  isClean: boolean;
  reason?: string;
  flaggedWord?: string;
} {
  if (!text || text.trim().length === 0) {
    return { isClean: true };
  }

  const lowerText = text.toLowerCase();

  // Check banned words
  for (const word of BANNED_WORDS) {
    // Use word boundaries to avoid false positives (e.g., "assistant" containing "ass")
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(lowerText)) {
      return {
        isClean: false,
        reason: 'Contains inappropriate language',
        flaggedWord: word
      };
    }
  }

  // Check suspicious patterns
  for (const pattern of SUSPICIOUS_PATTERNS) {
    if (pattern.test(text)) {
      return {
        isClean: false,
        reason: 'Contains potentially inappropriate content',
      };
    }
  }

  return { isClean: true };
}

/**
 * Moderate display name - stricter rules for public-facing names
 */
export function moderateDisplayName(name: string): {
  isClean: boolean;
  reason?: string;
} {
  const result = moderateContent(name);
  if (!result.isClean) {
    return result;
  }

  // Check for spam patterns
  if (isSpam(name)) {
    return {
      isClean: false,
      reason: 'Display name appears to be spam'
    };
  }

  return { isClean: true };
}

/**
 * Moderate bio/description text
 */
export function moderateBio(bio: string): {
  isClean: boolean;
  reason?: string;
} {
  const result = moderateContent(bio);
  if (!result.isClean) {
    return result;
  }

  // Check for spam patterns
  if (isSpam(bio)) {
    return {
      isClean: false,
      reason: 'Bio appears to contain spam or scam content'
    };
  }

  return { isClean: true };
}

/**
 * Moderate chat messages
 * Same as general content - hate speech and harmful content only
 * Profanity is fine, this is an adult platform
 */
export function moderateChatMessage(message: string): {
  isClean: boolean;
  reason?: string;
} {
  return moderateContent(message);
}

/**
 * Check if text looks like spam/scam
 */
export function isSpam(text: string): boolean {
  const lowerText = text.toLowerCase();

  // Only flag obvious spam/scams
  const spamIndicators = [
    'cashapp me', 'venmo me', 'paypal me',
    'send money', 'wire transfer',
    'nigerian prince', 'inheritance',
    'click this link', 'bit.ly', 'tinyurl',
  ];

  let spamScore = 0;
  for (const indicator of spamIndicators) {
    if (lowerText.includes(indicator)) {
      spamScore += 2; // Higher weight for clear spam
    }
  }

  // Check for phone numbers (potential spam)
  if (/\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/.test(text)) {
    spamScore++;
  }

  // Check for email addresses in display names (unusual)
  if (/\b[\w.-]+@[\w.-]+\.\w{2,}\b/.test(text)) {
    spamScore++;
  }

  return spamScore >= 2;
}

/**
 * Log flagged content for admin review
 * This would typically send to your backend for storage
 */
export async function reportFlaggedContent(
  userId: string,
  contentType: 'display_name' | 'bio' | 'message' | 'custom_game',
  content: string,
  reason: string
): Promise<void> {
  // TODO: Implement backend logging
  logger.warn('🚩 Flagged content:', {
    userId,
    contentType,
    reason,
    content: content.substring(0, 100) // Log only first 100 chars
  });

  // In production, send this to your backend:
  // await supabase.from('flagged_content').insert({
  //   user_id: userId,
  //   content_type: contentType,
  //   content: content,
  //   reason: reason,
  //   flagged_at: new Date().toISOString()
  // });
}